import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ExternalLink, Upload, EyeOff, Eye } from 'lucide-react';
import { platformsAPI } from '../../utils/api';
import { PageHeader, Modal, FormField, EmptyState, ConfirmDialog } from '../../components/layout/UI';
import { useToast, apiError } from '../../components/Toast';

/**
 * frontend/src/pages/admin/AdminPlatformsPage.jsx
 *
 * Where the directory is maintained. The user app only reads it.
 *
 * Environment belongs to Systems only, so the field appears and disappears
 * with the category — and switching to Tool clears whatever was chosen, which
 * the API enforces too.
 */

const ENVIRONMENTS = ['Development', 'UAT', 'Staging', 'Production'];

const emptyForm = () => ({
  name: '', description: '', link: '', category: 'Internal',
  environment: '', logo_url: '', sort_order: 0, is_active: true,
});

export default function AdminPlatformsPage() {
  const toast = useToast();
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [modalOpen, setModal] = useState(false);
  const [editItem, setEdit] = useState(null);
  const [form, setForm]     = useState(emptyForm());
  const [logoFile, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDel]  = useState(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      // include_inactive so disabled entries can be re-enabled here.
      const r = await platformsAPI.getAll({ include_inactive: 'true' });
      setItems(r.data || []);
    } catch (e) {
      toast.error(apiError(e, 'Could not load platforms.'));
    } finally { setLoad(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(emptyForm()); setLogo(null); setModal(true); };
  const openEdit = (p) => {
    setEdit(p);
    setForm({
      ...emptyForm(),
      ...p,
      environment: p.environment || '',
      description: p.description || '',
      logo_url: p.logo_url || '',
    });
    setLogo(null);
    setModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // A file upload needs multipart; everything else is plain JSON.
      let payload = { ...form };
      if (logoFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('logo', logoFile);
        payload = fd;
      }
      editItem ? await platformsAPI.update(editItem.id, payload)
               : await platformsAPI.create(payload);
      toast.success(editItem ? 'Platform updated' : 'Platform added');
      setModal(false);
    } catch (e) {
      toast.error(apiError(e, 'Could not save that platform.'));
    } finally { setSaving(false); load(); }
  };

  const handleDelete = async () => {
    try {
      await platformsAPI.delete(deleteId);
      toast.success('Platform deleted');
    } catch (e) {
      toast.error(apiError(e, 'Could not delete that platform.'));
    } finally { setDel(null); load(); }
  };

  /** Hiding is reversible; deleting is not. Offer both. */
  const toggleActive = async (p) => {
    try {
      await platformsAPI.update(p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? `${p.name} hidden from users` : `${p.name} is visible again`);
    } catch (e) {
      toast.error(apiError(e, 'Could not change that.'));
    } finally { load(); }
  };

  const Row = ({ p }) => (
    <tr className="border-b last:border-0" style={{ borderColor: 'var(--border)', opacity: p.is_active ? 1 : 0.55 }}>
      <td className="px-4 py-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden"
          style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
          {p.logo_url
            ? <img src={p.logo_url} alt="" className="w-full h-full object-contain" />
            : <span className="text-xs font-bold" style={{ color: '#2E539F' }}>{p.name[0]?.toUpperCase()}</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{p.name}</p>
        {p.description && (
          <p className="text-xs truncate max-w-md" style={{ color: 'var(--text-secondary)' }}>{p.description}</p>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ background: p.category === 'External' ? 'rgba(46,83,159,.10)' : 'rgba(92,195,188,.14)',
                   color: p.category === 'External' ? '#2E539F' : '#2f8a84' }}>
          {p.category}
        </span>
      </td>
      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
        {p.environment || '—'}
      </td>
      <td className="px-4 py-3">
        <a href={p.link} target="_blank" rel="noopener noreferrer"
          className="text-xs inline-flex items-center gap-1 hover:text-brand-500"
          style={{ color: 'var(--text-secondary)' }}>
          {p.link.replace(/^https?:\/\//, '').slice(0, 34)} <ExternalLink size={11} />
        </a>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => toggleActive(p)} title={p.is_active ? 'Hide from users' : 'Show to users'}
            className="p-1.5 rounded" style={{ color: 'var(--text-secondary)' }}>
            {p.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button onClick={() => openEdit(p)} title="Edit"
            className="p-1.5 rounded" style={{ color: 'var(--text-secondary)' }}><Pencil size={14} /></button>
          <button onClick={() => setDel(p.id)} title="Delete"
            className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 size={14} /></button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      <PageHeader title="Platforms" subtitle="Systems and tools shown to everyone in the user app">
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus size={15} /> Add Platform
        </button>
      </PageHeader>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !items.length ? (
        <EmptyState icon={Plus} title="No platforms yet"
          message="Add the systems and tools your team uses. They appear as tiles under Platforms in the user app." />
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full" style={{ minWidth: 760 }}>
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }}>
                {['', 'Name', 'Category', 'Environment', 'Link', ''].map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider"
                    style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{items.map(p => <Row key={p.id} p={p} />)}</tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModal(false)}
        title={editItem ? 'Edit Platform' : 'Add Platform'}>
        <div className="space-y-4">
          <FormField label="Name" required>
            <input className="input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Q-Lana LMS" />
          </FormField>

          <FormField label="Link" required>
            <input className="input" value={form.link}
              onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
              placeholder="https://…" />
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              Must start with http:// or https:// — a bare domain would resolve inside this app.
            </p>
          </FormField>

          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What is it for?" />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Category" required>
              <select className="select" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="Internal">Internal — software we use</option>
                <option value="External">External — a platform we build</option>
              </select>
            </FormField>

            <FormField label="Environment">
                <select className="select" value={form.environment}
                  onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}>
                  <option value="">Not environment-specific</option>
                {ENVIRONMENTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                Leave blank unless this link points at one environment of
                something you build. A product normally has three entries:
                Development, UAT or Staging, and Production.
              </p>
            </FormField>
          </div>

          <FormField label="Logo">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
                {logoFile
                  ? <img src={URL.createObjectURL(logoFile)} alt="" className="w-full h-full object-contain" />
                  : form.logo_url
                    ? <img src={form.logo_url} alt="" className="w-full h-full object-contain" />
                    : <Upload size={16} style={{ color: 'var(--text-secondary)' }} />}
              </div>
              <div className="flex-1">
                <input type="file" accept="image/*" className="input text-xs"
                  onChange={e => setLogo(e.target.files?.[0] || null)} />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Or paste an image URL below. Without a logo the tile shows the initials.
                </p>
              </div>
            </div>
            <input className="input mt-2" value={form.logo_url}
              onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
              placeholder="https://…/logo.png" />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Sort order">
              <input type="number" className="input" value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
            </FormField>
            <FormField label="Visible to users">
              <select className="select" value={form.is_active ? 'yes' : 'no'}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'yes' }))}>
                <option value="yes">Yes</option>
                <option value="no">No — hidden</option>
              </select>
            </FormField>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} className="btn-primary flex items-center gap-2"
              disabled={saving || !form.name.trim() || !form.link.trim()}>
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
              {editItem ? 'Save changes' : 'Add platform'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDel(null)} onConfirm={handleDelete}
        title="Delete platform"
        message="Delete this platform? It disappears for everyone. To take it down temporarily, hide it instead." />
    </div>
  );
}
